import { useEffect, useState } from "react";
import { apiPatch, tryApiGet } from "../api/client";
import { useAppData } from "../state/AppData";
import { colorFor } from '../lib/definitions/palette';
import { HAZARD_SEVERITY_ORDER, HAZARD_SEVERITY_LABEL, hazardAdapterStatusInfo, isHazardSeverity, type HazardSeverity } from '../lib/definitions/hazards';
import { type Protocol } from '../lib/definitions/protocols';
import { relativeTime } from "../lib/format";
import type { HazardAdapter, HazardEvent, HazardEventsResponse } from "../api/types";

// Alerts > Alerts (the hazard-driven half of Broadcasts/Alerts — see
// HazardsPage.tsx's own header comment for the full tab structure). One
// guided card per real hazard family, editable against config that
// already exists and is already generically PATCHable — the same
// mechanism the Settings > Regions editor proved out, not a new endpoint:
//
//   hazards.sources[]        (id, type, enabled, label, family,
//                             min_severity, poll_interval_s, ...)
//   hazards.region_routes[]  (family, region_id, transport, channel, enabled)
//
// Both are real GET /api/config/schema list_sections, but — verified
// directly against the live backend, correcting an earlier assumption
// that they patch exactly like `regions` does — they are NOT top-level
// bare-list sections. Only `regions` is (config_schema.top_level_list_
// sections()); `hazards.sources`/`hazards.region_routes` are lists
// NESTED inside the dict-shaped `hazards` section, so PATCH /api/config/
// hazards.sources with an array body 422s ("patch must be a mapping").
// The real, live-confirmed write path is PATCH /api/config/hazards (the
// PARENT section) with a dict patch naming just the one key to replace,
// e.g. {"patch": {"sources": [...]}} — ConfigManager.update_section's
// own _deep_merge leaves every other key of `hazards` (region_routes,
// poll_defaults, ...) untouched, confirmed by a live no-op round-trip.
//
// Region-based per-region channel overrides are NOT built here — the
// region model uses bounding box areas, not anchorpoints; the
// instruction was not to build region-shaped UI until that contract
// lands. Each card only edits the family's wildcard ("*") route cell,
// which every family already has for both protocols today.
//
// Severity is a fixed 3-value enum with no dropdown — rendered as a tab
// row instead.
type Loadable<T> = T | null | "unavailable";

interface HazardSourceItem {
  id: string;
  type?: string;
  enabled: boolean;
  label: string;
  family: string;
  min_severity: string;
  poll_interval_s: number;
  [k: string]: unknown;
}

interface HazardRouteItem {
  family: string;
  region_id: string;
  transport: string;
  channel: string;
  enabled: boolean;
  [k: string]: unknown;
}

// Just enough of GET /api/config/schema's shape to read/write two
// list_sections — the full response is SettingsPage's own concern (a
// larger, page-local type); this is intentionally partial, same "don't
// over-type a shape owned elsewhere" convention as AppData.tsx's own
// ConfigSchemaSlice.
interface ConfigSchemaListSections {
  list_sections?: { path: string; value?: unknown }[];
}

function useHazardConfig() {
  const [sources, setSources] = useState<Loadable<HazardSourceItem[]>>(null);
  const [routes, setRoutes] = useState<Loadable<HazardRouteItem[]>>(null);

  useEffect(() => {
    let cancelled = false;
    tryApiGet<ConfigSchemaListSections>("/api/config/schema")
      .then((res) => {
        if (cancelled || !res) {
          if (!cancelled) {
            setSources("unavailable");
            setRoutes("unavailable");
          }
          return;
        }
        const sourcesSection = res.list_sections?.find((s) => s.path === "hazards.sources");
        const routesSection = res.list_sections?.find((s) => s.path === "hazards.region_routes");
        setSources(Array.isArray(sourcesSection?.value) ? (sourcesSection!.value as HazardSourceItem[]) : []);
        setRoutes(Array.isArray(routesSection?.value) ? (routesSection!.value as HazardRouteItem[]) : []);
      })
      .catch(() => {
        if (!cancelled) {
          setSources("unavailable");
          setRoutes("unavailable");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { sources, setSources, routes, setRoutes };
}

function FamilyCard({
  protocol,
  source,
  route,
  adapter,
  onSaveSource,
  onSaveRoute,
}: {
  protocol: Protocol;
  source: HazardSourceItem;
  route: HazardRouteItem | null;
  adapter: HazardAdapter | undefined;
  onSaveSource: (next: HazardSourceItem) => Promise<void>;
  onSaveRoute: (next: HazardRouteItem) => Promise<void>;
}) {
  const { effectiveTheme } = useAppData();
  const [enabled, setEnabled] = useState(source.enabled);
  const [severity, setSeverity] = useState<HazardSeverity>(isHazardSeverity(source.min_severity) ? source.min_severity : "routine");
  const [pollInterval, setPollInterval] = useState(source.poll_interval_s);
  const [channel, setChannel] = useState(route?.channel ?? "");
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [preview, setPreview] = useState<HazardEvent | null | "loading" | "empty">(null);

  async function loadPreview() {
    setPreview("loading");
    const res = await tryApiGet<HazardEventsResponse>(`/api/hazards/events?window=30d&adapter=${encodeURIComponent(source.id)}&limit=1`);
    setPreview(res?.events?.[0] ?? "empty");
  }

  async function onSave() {
    setSaving(true);
    setSaveMsg(null);
    try {
      await onSaveSource({ ...source, enabled, min_severity: severity, poll_interval_s: pollInterval });
      if (route) await onSaveRoute({ ...route, channel });
      setSaveMsg({ kind: "ok", text: "Saved." });
    } catch (e) {
      setSaveMsg({ kind: "error", text: e instanceof Error ? e.message : "Save failed." });
    } finally {
      setSaving(false);
    }
  }

  const statusInfo = adapter ? hazardAdapterStatusInfo(adapter.last_status) : null;

  return (
    <div className="broadcast-rule">
      <div className="broadcast-rule-header">
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <strong>{source.label || source.family}</strong>
        </label>
        {statusInfo && adapter && (
          <span className="settings-field-desc">
            <span className="badge" style={{ background: colorFor(statusInfo.color, effectiveTheme) }}>
              {statusInfo.label}
            </span>{" "}
            last run {relativeTime(adapter.last_run_at)}
          </span>
        )}
      </div>

      <div className="settings-field">
        <span>Minimum severity to alert on</span>
        <div className="theme-toggle" role="group" aria-label={`${source.family} severity`}>
          {HAZARD_SEVERITY_ORDER.map((s) => (
            <button key={s} type="button" data-active={severity === s} onClick={() => setSeverity(s)}>
              {HAZARD_SEVERITY_LABEL[s]}
            </button>
          ))}
        </div>
        <span className="settings-field-desc">
          The one throttle this family has — there is no second, upstream filter; raising this is the only way to
          quiet a noisy source.
        </span>
      </div>

      <div className="settings-field">
        <span>Poll interval (seconds)</span>
        <input type="number" min={1} value={pollInterval} onChange={(e) => setPollInterval(Number(e.target.value))} />
        <span className="settings-field-desc">How often this source is fetched under normal conditions.</span>
      </div>

      <div className="settings-field">
        <span>Broadcast channel ({protocol === "meshtastic" ? "Meshtastic" : "MeshCore"})</span>
        <input
          type="text"
          value={channel}
          placeholder={protocol === "meshtastic" ? "e.g. LongFast" : "e.g. #alerts"}
          onChange={(e) => setChannel(e.target.value)}
          disabled={!route}
        />
        <span className="settings-field-desc">
          {route
            ? "The default channel this family broadcasts to on this protocol when no region-specific override applies."
            : "No routing cell exists yet for this family on this protocol."}
        </span>
      </div>

      <div className="node-detail-note">
        Region-specific channel overrides aren't editable here yet — the region model is being changed from anchor
        points back to bounding-box areas; this section only edits each family's default ("*") channel until that
        lands.
      </div>

      <div className="settings-section-actions">
        <button type="button" className="settings-save-btn" onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button type="button" className="settings-save-btn" onClick={loadPreview}>
          Preview most recent alert
        </button>
        {saveMsg && (
          <span className="settings-save-status" data-kind={saveMsg.kind}>
            {saveMsg.text}
          </span>
        )}
      </div>

      {preview === "loading" && <div className="node-detail-note">Loading a real recent event…</div>}
      {preview === "empty" && <div className="node-detail-note">No recent event from this source in the last 30 days to preview.</div>}
      {preview && preview !== "loading" && preview !== "empty" && (
        <div className="broadcast-preview">
          <div className="page-section-title" style={{ fontSize: 11 }}>
            Most recent alert text, verbatim
          </div>
          <div className="node-detail-json">{preview.rendered_message}</div>
        </div>
      )}
    </div>
  );
}

export function AlertsFamiliesPanel({ protocol, adapters }: { protocol: Protocol; adapters: HazardAdapter[] }) {
  const { sources, setSources, routes, setRoutes } = useHazardConfig();

  async function saveSource(next: HazardSourceItem) {
    if (!Array.isArray(sources)) return;
    const nextList = sources.map((s) => (s.id === next.id ? next : s));
    await apiPatch(`/api/config/hazards`, { patch: { sources: nextList } });
    setSources(nextList);
  }

  async function saveRoute(next: HazardRouteItem) {
    if (!Array.isArray(routes)) return;
    const nextList = routes.map((r) =>
      r.family === next.family && r.transport === next.transport && r.region_id === next.region_id ? next : r,
    );
    await apiPatch(`/api/config/hazards`, { patch: { region_routes: nextList } });
    setRoutes(nextList);
  }

  return (
    <section className="page-section">
      <div className="page-section-title">{protocol === "meshtastic" ? "Meshtastic" : "MeshCore"}</div>
      {sources === null && <div className="node-detail-note">Loading…</div>}
      {sources === "unavailable" && <div className="node-detail-note">Settings schema didn't respond as expected.</div>}
      {Array.isArray(sources) &&
        (sources.length === 0 ? (
          <div className="empty-state">No hazard sources configured on this instance.</div>
        ) : (
          sources.map((s) => (
            <FamilyCard
              key={s.id}
              protocol={protocol}
              source={s}
              route={Array.isArray(routes) ? routes.find((r) => r.family === s.family && r.transport === protocol && r.region_id === "*") ?? null : null}
              adapter={adapters.find((a) => a.id === s.id)}
              onSaveSource={saveSource}
              onSaveRoute={saveRoute}
            />
          ))
        ))}
    </section>
  );
}
